import ApplicationController from "./application_controller"
import Logger from '../lib/logger'
import PushSubscriptionManager from "../services/push_subscription_manager"

/**
 * NotificationPreferencesController
 * 
 * Manages the notification preferences UI, handling toggles, 
 * quiet hours, digest settings, and push notifications.
 * 
 * @example
 *   <div data-controller="notification-preferences"
 *        data-notification-preferences-preferences-value='{"browser":{}}'
 *        data-notification-preferences-browser-enabled-value="true">
 *     <!-- content -->
 *   </div>
 */
export default class extends ApplicationController {
  static targets = [
    "browserMaster",
    "emailMaster",
    "soundMaster",
    "checkbox",
    "quietHoursEnabled",
    "quietHoursSettings",
    "quietHoursStart",
    "quietHoursEnd",
    "digestEnabled",
    "digestSettings",
    "digestFrequency",
    "digestHour",
    "pushStatus",
    "pushToggleBtn",
    "testPushBtn",
    "saveStatus",
    "soundSettings",
    "soundOption"
  ]
  
  static values = {
    preferences: Object,
    browserEnabled: Boolean,
    emailEnabled: Boolean,
    soundEnabled: Boolean
  }
  
  connect() {
    this.pushManager = null
    this.audioPreview = null
    this.initializePushNotifications()
    this.updateQuietHoursVisibility()
    this.updateDigestVisibility()
    this.initializeAudioContext()
  }
  
  disconnect() {
    // Cleanup audio if playing
    if (this.audioPreview) {
      this.audioPreview.pause()
      this.audioPreview = null
    }
  }

  /**
   * Initialize audio context for oscillator fallback
   */
  initializeAudioContext() {
    // Will be created on first user interaction to comply with browser policies
    this.audioContext = null
  }
  
  /**
   * Initialize push notification support
   */
  async initializePushNotifications() {
    if (!this.hasPushStatusTarget) return
    
    // Check browser support
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      this.updatePushStatus("unsupported", "Push notifications are not supported in this browser")
      return
    }
    
    try {
      this.pushManager = new PushSubscriptionManager()
      const status = await this.pushManager.getSubscriptionStatus()
      
      if (status.permission === "denied") {
        this.updatePushStatus("denied", "Push notifications are blocked. Please enable them in browser settings.")
      } else if (status.subscribed) {
        this.updatePushStatus("subscribed", "Push notifications are enabled")
        this.showTestButton()
      } else {
        this.updatePushStatus("available", "Push notifications are available")
      }
    } catch (error) {
      Logger.error("Error initializing push notifications:", error)
      this.updatePushStatus("error", "Error checking push notification status")
    }
  }
  
  /**
   * Update push status display
   */
  updatePushStatus(status, message) {
    if (!this.hasPushStatusTarget) return
    
    const badges = {
      unsupported: '<span class="badge bg-secondary">Not Supported</span>',
      denied: '<span class="badge bg-danger">Blocked</span>',
      subscribed: '<span class="badge bg-success">Enabled</span>',
      available: '<span class="badge bg-info">Available</span>',
      error: '<span class="badge bg-warning">Error</span>'
    }
    
    this.pushStatusTarget.innerHTML = badges[status] || badges.error
    this.pushStatusTarget.title = message
    
    // Update button state
    if (this.hasPushToggleBtnTarget) {
      if (status === "unsupported" || status === "denied") {
        this.pushToggleBtnTarget.disabled = true
      } else {
        this.pushToggleBtnTarget.disabled = false
        this.pushToggleBtnTarget.textContent = status === "subscribed" 
          ? "Disable Push Notifications"
          : "Enable Push Notifications"
      }
    }
  }
  
  /**
   * Show test push button
   */
  showTestButton() {
    if (this.hasTestPushBtnTarget) {
      this.testPushBtnTarget.classList.remove("d-none")
      this.testPushBtnTarget.classList.add("d-inline-block")
    }
  }
  
  /**
   * Toggle push notifications
   */
  async togglePushNotifications() {
    if (!this.pushManager) return
    
    try {
      const status = await this.pushManager.getSubscriptionStatus()
      
      if (status.subscribed) {
        await this.pushManager.unsubscribe()
        this.updatePushStatus("available", "Push notifications disabled")
        if (this.hasTestPushBtnTarget) {
          this.testPushBtnTarget.classList.add("d-none")
          this.testPushBtnTarget.classList.remove("d-inline-block")
        }
      } else {
        await this.pushManager.subscribe()
        this.updatePushStatus("subscribed", "Push notifications enabled")
        this.showTestButton()
      }
      
      this.showSaveStatus("Push settings updated")
    } catch (error) {
      Logger.error("Error toggling push notifications:", error)
      this.showSaveStatus("Error updating push settings", true)
    }
  }
  
  /**
   * Send test push notification
   */
  async testPushNotification() {
    if (!this.pushManager) return
    
    try {
      this.testPushBtnTarget.disabled = true
      await this.pushManager.test()
      this.showSaveStatus("Test notification sent")
    } catch (error) {
      Logger.error("Error sending test notification:", error)
      this.showSaveStatus("Error sending test notification", true)
    } finally {
      if (this.hasTestPushBtnTarget) {
        this.testPushBtnTarget.disabled = false
      }
    }
  }
  
  /**
   * Toggle master channel setting
   */
  async toggleMasterChannel(event) {
    const channel = event.target.dataset.channel
    const enabled = event.target.checked
    
    try {
      const response = await this.safeFetch("/notification_preferences/toggle_channel", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ channel, enabled })
      })

      if (!response.ok) throw new Error("Failed to update preference")

      // Update all checkboxes for this channel
      this.checkboxTargets.forEach(checkbox => {
        if (checkbox.dataset.channel === channel) {
          checkbox.disabled = !enabled
        }
      })
      
      this.showSaveStatus(`${this.capitalize(channel)} notifications ${enabled ? 'enabled' : 'disabled'}`)
    } catch (error) {
      Logger.error("Error updating master channel:", error)
      event.target.checked = !enabled // Revert
      this.showSaveStatus("Error updating preference", true)
    }
  }
  
  /**
   * Toggle individual preference
   */
  async togglePreference(event) {
    const channel = event.target.dataset.channel
    const actionType = event.target.dataset.actionType
    const enabled = event.target.checked
    
    try {
      const response = await this.safeFetch("/notification_preferences/toggle_channel", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ channel, action_type: actionType, enabled })
      })
      
      if (!response.ok) throw new Error("Failed to update preference")
      
      this.showSaveStatus("Preference saved")
    } catch (error) {
      Logger.error("Error updating preference:", error)
      event.target.checked = !enabled // Revert
      this.showSaveStatus("Error saving preference", true)
    }
  }
  
  /**
   * Toggle quiet hours
   */
  toggleQuietHours(event) {
    this.updateQuietHoursVisibility()
    if (event) {
      this.updateQuietHours()
    }
  }
  
  /**
   * Update quiet hours visibility
   */
  updateQuietHoursVisibility() {
    const enabled = this.hasQuietHoursEnabledTarget && this.quietHoursEnabledTarget.checked
    
    this.quietHoursSettingsTargets.forEach(el => {
      el.classList.toggle("d-none", !enabled)
    })
  }
  
  /**
   * Update quiet hours settings
   */
  async updateQuietHours() {
    if (!this.hasQuietHoursEnabledTarget) return
    
    const quietHours = {
      enabled: this.quietHoursEnabledTarget.checked,
      start_hour: this.hasQuietHoursStartTarget ? parseInt(this.quietHoursStartTarget.value) : 22,
      end_hour: this.hasQuietHoursEndTarget ? parseInt(this.quietHoursEndTarget.value) : 8
    }
    
    try {
      const response = await this.safeFetch("/notification_preferences/update_quiet_hours", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ quiet_hours: quietHours })
      })
      
      if (!response.ok) throw new Error("Failed to update quiet hours")
      
      this.showSaveStatus("Quiet hours updated")
    } catch (error) {
      Logger.error("Error updating quiet hours:", error)
      this.showSaveStatus("Error updating quiet hours", true)
    }
  }
  
  /**
   * Toggle digest settings
   */
  toggleDigest(event) {
    this.updateDigestVisibility()
    if (event) {
      this.updateDigest()
    }
  }
  
  /**
   * Update digest settings visibility
   */
  updateDigestVisibility() {
    const enabled = this.hasDigestEnabledTarget && this.digestEnabledTarget.checked
    
    this.digestSettingsTargets.forEach(el => {
      el.classList.toggle("d-none", !enabled)
    })
  }
  
  /**
   * Update digest settings
   */
  async updateDigest() {
    if (!this.hasDigestEnabledTarget) return
    
    const digest = {
      enabled: this.digestEnabledTarget.checked,
      frequency: this.hasDigestFrequencyTarget ? this.digestFrequencyTarget.value : "daily",
      preferred_hour: this.hasDigestHourTarget ? parseInt(this.digestHourTarget.value) : 9
    }
    
    try {
      const response = await this.safeFetch("/notification_preferences/update_digest", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ digest })
      })
      
      if (!response.ok) throw new Error("Failed to update digest settings")
      
      this.showSaveStatus("Digest settings updated")
    } catch (error) {
      Logger.error("Error updating digest:", error)
      this.showSaveStatus("Error updating digest settings", true)
    }
  }
  
  /**
   * Reset all preferences to defaults
   */
  async resetDefaults(event) {
    const confirmed = confirm(event.target.dataset.confirm || "Reset all preferences?")
    if (!confirmed) return
    
    try {
      const response = await this.safeFetch("/notification_preferences/reset", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        }
      })
      
      if (!response.ok) throw new Error("Failed to reset preferences")
      
      // Reload page to show new defaults
      window.location.reload()
    } catch (error) {
      Logger.error("Error resetting preferences:", error)
      this.showSaveStatus("Error resetting preferences", true)
    }
  }
  
  /**
   * Show save status message
   */
  showSaveStatus(message, isError = false) {
    if (!this.hasSaveStatusTarget) return
    
    this.saveStatusTarget.textContent = message
    this.saveStatusTarget.classList.toggle("text-danger", isError)
    this.saveStatusTarget.classList.toggle("text-success", !isError && message !== "All changes are saved automatically")
    this.saveStatusTarget.classList.toggle("text-muted", message === "All changes are saved automatically")
    
    // Reset after delay
    setTimeout(() => {
      if (this.hasSaveStatusTarget) {
        this.saveStatusTarget.textContent = "All changes are saved automatically"
        this.saveStatusTarget.classList.remove("text-danger", "text-success")
        this.saveStatusTarget.classList.add("text-muted")
      }
    }, 3000)
  }
  
  /**
   * Get CSRF token
   */
  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
  
  /**
   * Capitalize first letter
   */
  capitalize(string) {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }

  // ========================================
  // SOUND SELECTION METHODS
  // ========================================

  /**
   * Preview a notification sound
   */
  previewSound(event) {
    event.stopPropagation() // Don't trigger select
    
    const soundId = event.currentTarget.dataset.soundId
    const soundUrl = event.currentTarget.dataset.soundUrl
    
    // Stop any currently playing preview
    if (this.audioPreview) {
      this.audioPreview.pause()
      this.audioPreview = null
    }
    
    if (soundId === 'oscillator' || !soundUrl) {
      // Play oscillator sound
      this.playOscillatorSound()
    } else {
      // Play audio file
      this.playAudioPreview(soundUrl)
    }
  }

  /**
   * Play audio file preview
   */
  playAudioPreview(url) {
    try {
      this.audioPreview = new Audio(url)
      this.audioPreview.volume = 0.5
      
      this.audioPreview.play().catch(error => {
        Logger.warn('Failed to play audio preview:', error)
        // Fallback to oscillator
        this.playOscillatorSound()
      })
    } catch (error) {
      Logger.warn('Error creating audio preview:', error)
      this.playOscillatorSound()
    }
  }

  /**
   * Play oscillator sound (Web Audio API)
   */
  playOscillatorSound() {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      // Pleasant notification sound pattern
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime)
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1)
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2)

      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3)

      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.3)
      
      Logger.debug('🔔 Oscillator sound played')
    } catch (error) {
      Logger.warn('Error playing oscillator sound:', error)
    }
  }

  /**
   * Select a notification sound
   */
  async selectSound(event) {
    const soundId = event.currentTarget.dataset.soundId
    
    // Update UI immediately for responsiveness
    this.soundOptionTargets.forEach(option => {
      const isSelected = option.dataset.soundId === soundId
      option.classList.toggle('border-primary', isSelected)
      
      // Update check icon
      const checkIcon = option.querySelector('.bi-check-circle-fill')
      const iconContainer = option.querySelector('.flex-shrink-0.ms-2')
      
      if (isSelected && !checkIcon) {
        if (iconContainer) {
          iconContainer.innerHTML = '<i class="bi bi-check-circle-fill text-primary"></i>'
        }
      } else if (!isSelected && checkIcon) {
        checkIcon.remove()
      }
    })
    
    // Save to server
    try {
      const response = await this.safeFetch("/notification_preferences/update_sound", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ sound: soundId })
      })
      
      if (!response.ok) throw new Error("Failed to update sound preference")
      
      const data = await response.json()
      this.showSaveStatus(`Sound changed to ${data.name}`)
    } catch (error) {
      Logger.error("Error updating sound preference:", error)
      this.showSaveStatus("Error updating sound preference", true)
    }
  }
}